import { resolvePhotoUrl, handlePhotoError } from "../../utils/profilePhoto";
import React, { useEffect, useMemo, useState } from "react";
import { MoreHorizontal, ThumbsUp, X, Send, Smile } from "lucide-react";
import { toast } from "react-toastify";
import {
  getMySurveyEngagement,
  getMySurveyEngagementReplies,
  likeMySurveyComment,
  replyMySurveyComment,
} from "../../service/mySurvey";


const resolveAvatar = resolvePhotoUrl;

const formatTime = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const normalizeComment = (comment) => ({
  ...comment,
  comment_id: Number(comment.comment_id ?? comment.id),
  reply_count: Number(comment.reply_count ?? 0),
  like_count: Number(comment.like_count ?? comment.likes_count ?? 0),
  liked_by_me: Boolean(comment.liked_by_me),
});

const normalizeLike = (like) => ({
  ...like,
  id: Number(like.id ?? like.member_id),
});

const MySurveyEngagementModal = ({ surveyId, activeTab, onTabChange, onClose }) => {
  const selectedTab = activeTab === "comments" ? "comments" : "likes";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [engagement, setEngagement] = useState(null);
  const [repliesByCommentId, setRepliesByCommentId] = useState({});
  const [repliesLoadingMap, setRepliesLoadingMap] = useState({});
  const [expandedComments, setExpandedComments] = useState(new Set());
  const [replyDrafts, setReplyDrafts] = useState({});
  const [replyTargets, setReplyTargets] = useState({});
  const [submittingReplyMap, setSubmittingReplyMap] = useState({});
  const [likingCommentMap, setLikingCommentMap] = useState({});

  const comments = useMemo(() => engagement?.comments || [], [engagement]);
  const likes = useMemo(() => engagement?.likes || [], [engagement]);
  const likesCount = engagement?.like_count ?? likes.length ?? 0;
  const commentsCount = engagement?.comment_count ?? comments.length ?? 0;

  const updateCommentLikeCounts = (commentId, newLikeCount, likedByMe) => {
    const patchComment = (comment) => {
      if (Number(comment.comment_id) === Number(commentId)) {
        return {
          ...comment,
          like_count: newLikeCount,
          liked_by_me: likedByMe,
        };
      }
      return comment;
    };

    setEngagement((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        comments: (prev.comments || []).map(patchComment),
      };
    });

    setRepliesByCommentId((prev) => {
      const next = { ...prev };
      let changed = false;

      Object.keys(next).forEach((rootId) => {
        const updatedReplies = (next[rootId] || []).map((reply) => {
          if (Number(reply.comment_id) === Number(commentId)) {
            changed = true;
            return {
              ...reply,
              like_count: newLikeCount,
              liked_by_me: likedByMe,
            };
          }
          return reply;
        });

        if (changed) {
          next[rootId] = updatedReplies;
        }
      });

      return changed ? next : prev;
    });
  };

  const fetchReplies = async (rootCommentId) => {
    if (!surveyId || !rootCommentId) return;

    try {
      setRepliesLoadingMap((prev) => ({ ...prev, [rootCommentId]: true }));
      const response = await getMySurveyEngagementReplies(surveyId, rootCommentId);
      if (response?.data?.success) {
        const replies = Array.isArray(response.data.data) ? response.data.data.map(normalizeComment) : [];
        setRepliesByCommentId((prev) => ({ ...prev, [rootCommentId]: replies }));
      }
    } catch (err) {
      console.error("Error fetching engagement replies:", err);
      toast.error("Failed to load replies.");
    } finally {
      setRepliesLoadingMap((prev) => ({ ...prev, [rootCommentId]: false }));
    }
  };

  useEffect(() => {
    if (!surveyId) return;

    const loadEngagement = async () => {
      try {
        setLoading(true);
        setError("");

        const response = await getMySurveyEngagement(surveyId);
        if (response?.data?.success) {
          const data = response.data.data || {};
          const normalizedComments = Array.isArray(data.comments) ? data.comments.map(normalizeComment) : [];
          const normalizedLikes = Array.isArray(data.likes) ? data.likes.map(normalizeLike) : [];

          setEngagement({
            ...data,
            comments: normalizedComments,
            likes: normalizedLikes,
            like_count: Number(data.like_count ?? normalizedLikes.length ?? 0),
            comment_count: Number(data.comment_count ?? normalizedComments.length ?? 0),
          });
          setRepliesByCommentId({});
          setExpandedComments(new Set());
          setReplyDrafts({});
          setReplyTargets({});
          setSubmittingReplyMap({});
          setLikingCommentMap({});
        } else {
          setError(response?.data?.message || "Failed to load engagement.");
        }
      } catch (err) {
        console.error("Error loading survey engagement:", err);
        setError(err.response?.data?.message || "Failed to load engagement.");
        toast.error("Failed to load survey engagement.");
      } finally {
        setLoading(false);
      }
    };

    loadEngagement();
  }, [surveyId]);

  const toggleCommentExpansion = async (commentId) => {
    const rootCommentId = Number(commentId);
    setExpandedComments((prev) => {
      const next = new Set(prev);
      if (next.has(rootCommentId)) {
        next.delete(rootCommentId);
      } else {
        next.add(rootCommentId);
      }
      return next;
    });

    if (!expandedComments.has(rootCommentId) && !repliesByCommentId[rootCommentId]) {
      await fetchReplies(rootCommentId);
    }
  };

  const handleLikeComment = async (commentId) => {
    if (!surveyId || !commentId || likingCommentMap[commentId]) return;

    try {
      setLikingCommentMap((prev) => ({ ...prev, [commentId]: true }));

      const currentComment =
        comments.find((item) => Number(item.comment_id) === Number(commentId)) ||
        Object.values(repliesByCommentId).flat().find((item) => Number(item.comment_id) === Number(commentId));

      const response = await likeMySurveyComment(surveyId, commentId);
      if (response?.data?.success) {
        const payload = response.data.data || {};
        updateCommentLikeCounts(
          commentId,
          Number(payload.total_likes ?? currentComment?.like_count ?? 0),
          Boolean(payload.liked ?? !currentComment?.liked_by_me)
        );
      } else {
        toast.error(response?.data?.message || "Failed to update comment like.");
      }
    } catch (err) {
      console.error("Error liking comment:", err);
      toast.error(err.response?.data?.message || "Failed to update comment like.");
    } finally {
      setLikingCommentMap((prev) => ({ ...prev, [commentId]: false }));
    }
  };

  const handleReplyTarget = (rootCommentId, targetComment) => {
    const targetId = Number(targetComment?.comment_id ?? rootCommentId);
    setReplyTargets((prev) => ({
      ...prev,
      [rootCommentId]: {
        commentId: targetId,
        memberName: targetComment?.member_name || targetComment?.creator_name || "this comment",
      },
    }));
    setReplyDrafts((prev) => ({ ...prev, [rootCommentId]: prev[rootCommentId] ?? "" }));
    setExpandedComments((prev) => new Set(prev).add(Number(rootCommentId)));
  };

  const handleSubmitReply = async (rootCommentId) => {
    const draft = replyDrafts[rootCommentId] || "";
    const target = replyTargets[rootCommentId];
    const replyToCommentId = Number(target?.commentId ?? rootCommentId);

    if (!draft.trim() || submittingReplyMap[rootCommentId]) return;

    try {
      setSubmittingReplyMap((prev) => ({ ...prev, [rootCommentId]: true }));

      const response = await replyMySurveyComment(surveyId, rootCommentId, {
        comment: draft.trim(),
        reply_to_comment_id: replyToCommentId,
      });

      if (response?.data?.success) {
        const reply = normalizeComment(response.data.data || {});
        setRepliesByCommentId((prev) => {
          const currentReplies = prev[rootCommentId] || [];
          return {
            ...prev,
            [rootCommentId]: [...currentReplies, reply],
          };
        });

        setEngagement((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            comment_count: Number(prev.comment_count || 0) + 1,
            comments: (prev.comments || []).map((comment) =>
              Number(comment.comment_id) === Number(rootCommentId)
                ? { ...comment, reply_count: Number(comment.reply_count || 0) + 1 }
                : comment
            ),
          };
        });

        setReplyDrafts((prev) => ({ ...prev, [rootCommentId]: "" }));
        setReplyTargets((prev) => ({
          ...prev,
          [rootCommentId]: {
            commentId: Number(rootCommentId),
            memberName: "this comment",
          },
        }));
        toast.success(response.data.message || "Reply posted successfully.");
      } else {
        toast.error(response?.data?.message || "Failed to post reply.");
      }
    } catch (err) {
      console.error("Error posting reply:", err);
      toast.error(err.response?.data?.message || "Failed to post reply.");
    } finally {
      setSubmittingReplyMap((prev) => ({ ...prev, [rootCommentId]: false }));
    }
  };

  const renderReply = (reply, rootCommentId, depth = 1) => {
    const replyId = Number(reply.comment_id);
    const childReplies = repliesByCommentId[replyId] || [];
    const isExpanded = expandedComments.has(replyId);

    return (
      <div key={replyId} className={`my-survey-engagement-comment depth-${depth}`}>
        <img src={resolveAvatar(reply.profile_photo)} onError={handlePhotoError} alt="" className="my-survey-engagement-avatar" />
        <div className="flex-grow-1">
          <div className="my-survey-engagement-header">
            <span className="my-survey-engagement-name">{reply.member_name || reply.creator_name || "User"}</span>
            <span className="my-survey-engagement-time">{formatTime(reply.created_at)}</span>
          </div>
          <div className="my-survey-engagement-comment">{reply.comment}</div>
          <div className="my-survey-engagement-actions">
            <button type="button" onClick={() => handleLikeComment(replyId)}>
              Like
            </button>
            <button type="button" onClick={() => handleReplyTarget(rootCommentId, reply)}>
              Reply
            </button>
          </div>

          {reply.like_count > 0 && (
            <div className="d-flex align-items-center gap-1 mt-2">
              <span className="sr-like-circle-badge">
                <ThumbsUp size={8} fill="currentColor" />
              </span>
              <span className="ft-12">{reply.like_count}</span>
            </div>
          )}

          {childReplies.length > 0 && (
            <div className="mt-3">
              <button
                type="button"
                className="border-0 bg-transparent p-0 ft-12 fw-semibold text-secondary"
                onClick={() => toggleCommentExpansion(replyId)}
              >
                {isExpanded ? "Hide nested replies" : `View ${childReplies.length} nested replies`}
              </button>

              {isExpanded && (
                <div className="my-survey-engagement-nested-replies">
                  {childReplies.map((nestedReply) => renderReply(nestedReply, rootCommentId, depth + 1))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderComment = (comment) => {
    const commentId = Number(comment.comment_id);
    const replies = repliesByCommentId[commentId] || [];
    const isExpanded = expandedComments.has(commentId);
    const replyTarget = replyTargets[commentId] || { commentId, memberName: "this comment" };
    const replyDraft = replyDrafts[commentId] || "";

    return (
      <div className="my-survey-engagement-thread" key={commentId}>
        <div className="my-survey-engagement-row align-items-start">
          <img src={resolveAvatar(comment.profile_photo)} onError={handlePhotoError} alt="" className="my-survey-engagement-avatar" />
          <div className="flex-grow-1">
            <div className="my-survey-engagement-header">
              <span className="my-survey-engagement-name">{comment.member_name || comment.creator_name || "User"}</span>
              <span className="my-survey-engagement-time">{formatTime(comment.created_at)}</span>
            </div>
            <div className="my-survey-engagement-comment">{comment.comment}</div>
            <div className="my-survey-engagement-actions">
              <button type="button" onClick={() => handleLikeComment(commentId)} disabled={likingCommentMap[commentId]}>
                {comment.liked_by_me ? "Unlike" : "Like"}
              </button>
              <button type="button" onClick={() => handleReplyTarget(commentId, comment)}>
                Reply
              </button>
            </div>

            <div className="d-flex align-items-center gap-3 mt-2">
              {comment.like_count > 0 && (
                <span className="sr-like-circle-badge">
                  <ThumbsUp size={8} fill="currentColor" />
                  <span className="ms-1">{comment.like_count}</span>
                </span>
              )}
              {comment.reply_count > 0 && (
                <button
                  type="button"
                  className="border-0 bg-transparent p-0 ft-12 fw-semibold text-secondary"
                  onClick={() => toggleCommentExpansion(commentId)}
                >
                  {isExpanded ? "Hide replies" : `View ${comment.reply_count} replies`}
                </button>
              )}
            </div>
          </div>

          <button type="button" className="border-0 bg-transparent p-0 text-black">
            <MoreHorizontal size={18} />
          </button>
        </div>

        {isExpanded && (
          <div className="my-survey-engagement-replies">
            {repliesLoadingMap[commentId] ? (
              <div className="py-2 text-secondary ft-12">Loading replies...</div>
            ) : replies.length > 0 ? (
              replies.map((reply) => renderReply(reply, commentId))
            ) : (
              <div className="py-2 text-secondary ft-12">No replies yet.</div>
            )}
          </div>
        )}

        <div className="my-survey-engagement-reply-composer">
          <div className="d-flex align-items-center gap-2 mb-2 ft-12 text-secondary">
            <Smile size={14} />
            <span>Replying to @{replyTarget.memberName}</span>
          </div>
          <div className="my-survey-engagement-reply-box">
            <textarea
              rows={2}
              placeholder="Write a reply..."
              value={replyDraft}
              onChange={(event) =>
                setReplyDrafts((prev) => ({
                  ...prev,
                  [commentId]: event.target.value,
                }))
              }
            />
            <button
              type="button"
              onClick={() => handleSubmitReply(commentId)}
              disabled={!replyDraft.trim() || submittingReplyMap[commentId]}
            >
              <Send size={14} />
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="custom-modal-overlay">
      <div className="my-survey-engagement-modal bg-white position-relative">
        <button
          type="button"
          className="position-absolute top-0 end-0 border-0 bg-transparent p-3 text-secondary"
          onClick={onClose}
          aria-label="Close"
        >
          <X size={22} />
        </button>

        <div className="my-survey-engagement-tabs">
          <button
            type="button"
            className={`my-survey-engagement-tab ${selectedTab === "likes" ? "active" : ""}`}
            onClick={() => onTabChange("likes")}
          >
            Likes ({String(likesCount).padStart(2, "0")})
          </button>
          <button
            type="button"
            className={`my-survey-engagement-tab ${selectedTab === "comments" ? "active" : ""}`}
            onClick={() => onTabChange("comments")}
          >
            Comments ({String(commentsCount).padStart(2, "0")})
          </button>
        </div>

        <div className="my-survey-engagement-body">
          {loading ? (
            <div className="py-5 text-center text-secondary">Loading engagement...</div>
          ) : error ? (
            <div className="py-5 text-center text-danger">{error}</div>
          ) : selectedTab === "likes" ? (
            <div className="my-survey-engagement-list">
              {likes.length > 0 ? (
                likes.map((item) => (
                  <div className="my-survey-engagement-row" key={item.id}>
                    <img src={resolveAvatar(item.profile_photo)} onError={handlePhotoError} alt="" className="my-survey-engagement-avatar" />
                    <div className="flex-grow-1">
                      <div className="my-survey-engagement-name">{item.full_name || item.member_name || "User"}</div>
                      <div className="my-survey-engagement-role">{item.job_title || item.company_name || ""}</div>
                      <div className="d-flex align-items-center gap-2 mt-1">
                        <span className="my-survey-engagement-time">{formatTime(item.liked_at)}</span>
                        <span className="sr-like-circle-badge">
                          <ThumbsUp size={8} fill="currentColor" />
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="py-4 text-center text-secondary">No likes yet.</div>
              )}
            </div>
          ) : (
            <div className="my-survey-engagement-list">
              {comments.length > 0 ? (
                comments.map((comment) => renderComment(comment))
              ) : (
                <div className="py-4 text-center text-secondary">No comments yet.</div>
              )}
            </div>
          )}
        </div>

        <div className="my-survey-engagement-footer">
          <button
            type="button"
            className="redhoverbtn bgwhite border rounded-2 px-5 py-3 ft-14 fw-bold"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default MySurveyEngagementModal;
